class TpShippingCalculator extends HTMLElement {
  constructor() {
    super();
    this.calculating = false;
  }

  connectedCallback() {
    this.input = this.querySelector('.js-shipping-input');
    this.button = this.querySelector('.js-shipping-btn');
    this.response = this.querySelector('.js-shipping-response');
    this.variantId = this.dataset.variantId;

    if (!this.input || !this.button) return;

    this.input.addEventListener('input', this.handleMask.bind(this));
    this.input.addEventListener('keyup', this.handleKeyup.bind(this));
    this.button.addEventListener('click', this.handleClick.bind(this));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data && event.data.variant) {
          this.variantId = String(event.data.variant.id);
        }
      });
    }
  }

  handleMask(e) {
    var raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 5) {
      raw = raw.slice(0, 5) + '-' + raw.slice(5, 8);
    }
    e.target.value = raw.slice(0, 9);
  }

  handleKeyup(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.calculate();
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.calculate();
  }

  async calculate() {
    if (this.calculating) return;

    var zip = this.input.value.trim();
    var zipClean = zip.replace('-', '');

    if (!zipClean || zipClean.length !== 8) {
      this.renderMessage('Digite um CEP válido.', true);
      return;
    }

    this.calculating = true;
    this.setButtonLoading(true);
    this.renderLoading();

    try {
      var zipInfo = await this.fetchZipInfo(zipClean);
      if (!zipInfo) {
        this.renderMessage('CEP não encontrado.', true);
        this.calculating = false;
        this.setButtonLoading(false);
        return;
      }

      var cart = await this.fetchCart();
      var itemWasInCart = cart.items.some(function (item) {
        return String(item.variant_id) === String(this.variantId);
      }.bind(this));

      var needsCleanup = false;
      if (!itemWasInCart && this.variantId) {
        await this.addToCart(this.variantId);
        needsCleanup = true;
      }

      var rates = await this.fetchShippingRates(zipInfo);

      if (needsCleanup) {
        await this.removeFromCart(this.variantId);
      }

      this.renderResults(zipInfo, rates);
      this.animateIn();
    } catch (err) {
      console.error('Erro ao calcular frete:', err);
      this.renderMessage('Erro ao buscar frete. Tente novamente.', true);
      this.animateIn();
    }

    this.calculating = false;
    this.setButtonLoading(false);
  }

  animateIn() {
    if (typeof gsap === 'undefined') return;
    var children = this.response.children;
    if (!children.length) return;
    gsap.fromTo(children,
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', stagger: 0.08 }
    );
  }

  setButtonLoading(state) {
    this.button.disabled = state;
    if (state) {
      this.buttonText = this.button.textContent;
      var w = this.button.offsetWidth;
      this.button.style.minWidth = w + 'px';
      this.button.innerHTML = '<span class="tp-shipping-calc__btn-spinner"></span>';
    } else {
      this.button.textContent = this.buttonText || 'CONSULTAR';
      this.button.style.minWidth = '';
    }
  }

  async fetchZipInfo(zip) {
    try {
      var res = await fetch('https://viacep.com.br/ws/' + zip + '/json');
      if (res.ok) {
        var data = await res.json();
        if (!data.erro) {
          data.state = this.ufToState(data.uf);
          return data;
        }
      }
    } catch (err) {
      console.error('Erro ViaCEP:', err);
    }

    // Fallback: inferir estado pela faixa numérica do CEP
    var uf = this.ufFromRange(parseInt(zip, 10));
    if (uf) {
      return {
        cep: zip.slice(0, 5) + '-' + zip.slice(5),
        uf: uf,
        state: this.ufToState(uf),
        localidade: '',
        logradouro: '',
        bairro: ''
      };
    }

    return null;
  }

  ufFromRange(num) {
    if (num >= 1000000  && num <= 19999999) return 'SP';
    if (num >= 20000000 && num <= 28999999) return 'RJ';
    if (num >= 29000000 && num <= 29999999) return 'ES';
    if (num >= 30000000 && num <= 39999999) return 'MG';
    if (num >= 40000000 && num <= 48999999) return 'BA';
    if (num >= 49000000 && num <= 49999999) return 'SE';
    if (num >= 50000000 && num <= 56999999) return 'PE';
    if (num >= 57000000 && num <= 57999999) return 'AL';
    if (num >= 58000000 && num <= 58999999) return 'PB';
    if (num >= 59000000 && num <= 59999999) return 'RN';
    if (num >= 60000000 && num <= 63999999) return 'CE';
    if (num >= 64000000 && num <= 64999999) return 'PI';
    if (num >= 65000000 && num <= 65999999) return 'MA';
    if (num >= 66000000 && num <= 68999999) return 'PA';
    if (num >= 69000000 && num <= 69899999) return 'AM';
    if (num >= 69900000 && num <= 69999999) return 'AC';
    if (num >= 70000000 && num <= 73699999) return 'DF';
    if (num >= 73700000 && num <= 76799999) return 'GO';
    if (num >= 76800000 && num <= 76999999) return 'RO';
    if (num >= 77000000 && num <= 77999999) return 'TO';
    if (num >= 78000000 && num <= 78999999) return 'MT';
    if (num >= 79000000 && num <= 79999999) return 'MS';
    if (num >= 80000000 && num <= 87999999) return 'PR';
    if (num >= 88000000 && num <= 89999999) return 'SC';
    if (num >= 90000000 && num <= 99999999) return 'RS';
    return null;
  }

  async fetchCart() {
    try {
      var res = await fetch('/cart.json');
      return await res.json();
    } catch (err) {
      return { items: [], item_count: 0 };
    }
  }

  async addToCart(variantId) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    });
  }

  async removeFromCart(variantId) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 0 })
    });
  }

  async fetchShippingRates(zipInfo) {
    var params = new URLSearchParams({
      'shipping_address[zip]': zipInfo.cep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': zipInfo.state
    });

    var res = await fetch('/cart/shipping_rates.json?' + params.toString());
    if (!res.ok) {
      var err = await res.json();
      throw new Error(err.error || 'Erro ao buscar frete');
    }
    var data = await res.json();
    return data.shipping_rates || [];
  }

  renderLoading() {
    this.response.innerHTML =
      '<div class="tp-shipping-calc__loading">' +
        '<div class="tp-shipping-calc__spinner"></div>' +
      '</div>';
  }

  renderMessage(text, isError) {
    this.response.innerHTML =
      '<p class="tp-shipping-calc__message' +
      (isError ? ' tp-shipping-calc__message--error' : '') +
      '">' + text + '</p>';
  }

  renderResults(zipInfo, rates) {
    var html = '';

    var addressParts = [];
    if (zipInfo.logradouro) addressParts.push(zipInfo.logradouro);
    if (zipInfo.bairro) addressParts.push(zipInfo.bairro);
    var cityState = '';
    if (zipInfo.localidade) cityState += zipInfo.localidade;
    if (zipInfo.uf) cityState += ' - ' + zipInfo.uf;
    if (addressParts.length > 0) {
      cityState = addressParts.join(', ') + ' - ' + cityState;
    }

    if (cityState) {
      html +=
        '<div class="tp-shipping-calc__address">' +
          '<svg class="tp-shipping-calc__address-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M8 1.333A4.67 4.67 0 0 0 3.333 6C3.333 9.5 8 14.667 8 14.667S12.667 9.5 12.667 6A4.67 4.67 0 0 0 8 1.333Zm0 6.334A1.667 1.667 0 1 1 8 4.333a1.667 1.667 0 0 1 0 3.334Z" fill="currentColor"/>' +
          '</svg>' +
          '<span class="tp-shipping-calc__address-text">' + this.escapeHtml(cityState) + '</span>' +
        '</div>';
    }

    if (!rates || rates.length === 0) {
      html += '<p class="tp-shipping-calc__message">Nenhuma opção de frete disponível para este endereço.</p>';
      this.response.innerHTML = html;
      return;
    }

    html += '<ul class="tp-shipping-calc__rates">';
    for (var i = 0; i < rates.length; i++) {
      var rate = rates[i];
      var isFree = Number(rate.price) === 0;
      var priceText = isFree ? 'Grátis' : this.formatPrice(rate.price);
      var priceClass = 'tp-shipping-calc__rate-price' + (isFree ? ' tp-shipping-calc__rate-price--free' : '');

      var daysText = this.getDeliveryText(rate);

      html +=
        '<li class="tp-shipping-calc__rate">' +
          '<div class="tp-shipping-calc__rate-info">' +
            '<span class="tp-shipping-calc__rate-name">' + this.escapeHtml(rate.presentment_name || rate.name) + '</span>' +
            (daysText ? '<span class="tp-shipping-calc__rate-days">' + this.escapeHtml(daysText) + '</span>' : '') +
          '</div>' +
          '<span class="' + priceClass + '">' + priceText + '</span>' +
        '</li>';
    }
    html += '</ul>';

    this.response.innerHTML = html;
  }

  getDeliveryText(rate) {
    if (rate.delivery_range && rate.delivery_range.length === 2) {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var msPerDay = 1000 * 60 * 60 * 24;
      var dateMin = new Date(rate.delivery_range[0] + 'T00:00:00');
      var dateMax = new Date(rate.delivery_range[1] + 'T00:00:00');
      var daysMin = Math.round((dateMin - today) / msPerDay);
      var daysMax = Math.round((dateMax - today) / msPerDay);
      if (daysMin === daysMax) {
        return 'em até ' + daysMin + ' dia' + (daysMin !== 1 ? 's' : '');
      }
      return 'em até ' + daysMin + ' a ' + daysMax + ' dias';
    }
    if (rate.delivery_days && rate.delivery_days.length > 0) {
      var min = rate.delivery_days[0];
      var max = rate.delivery_days[rate.delivery_days.length - 1];
      if (min === max) return min + ' dia' + (min > 1 ? 's' : '') + ' útil' + (min > 1 ? '' : '');
      return min + ' a ' + max + ' dias úteis';
    }
    return '';
  }

  formatPrice(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  ufToState(uf) {
    var map = {
      AC: 'Acre', AL: 'Alagoas', AM: 'Amazonas', AP: 'Amapá',
      BA: 'Bahia', CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo',
      GO: 'Goiás', MA: 'Maranhão', MG: 'Minas Gerais', MS: 'Mato Grosso do Sul',
      MT: 'Mato Grosso', PA: 'Pará', PB: 'Paraíba', PE: 'Pernambuco',
      PI: 'Piauí', PR: 'Paraná', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte',
      RO: 'Rondônia', RR: 'Roraima', RS: 'Rio Grande do Sul', SC: 'Santa Catarina',
      SE: 'Sergipe', SP: 'São Paulo', TO: 'Tocantins'
    };
    return map[uf] || uf;
  }
}

customElements.define('tp-shipping-calculator', TpShippingCalculator);
